import { useEffect, useRef, useState } from "react";

export type TrailPoint = { x: number; y: number; addedAt: number };

const TRAIL_CAP = 20;
const MOVEMENT_EPSILON = 1e-6;

/**
 * In-memory trail of past Current coords. Reset whenever `resetKey`
 * changes (typical key: `${flavorId}:${pin.hotspotWeight}`). Appends a
 * new point whenever `current` moves by > 1e-6 in x or y. Trail is
 * capped at 20 points, FIFO.
 *
 * Returned array is the breadcrumb list oldest → newest. Render the
 * last element under the Current marker (suppress its own dot).
 *
 * **Reset key composition.** The locked visual spec specifies the resetKey
 * as `${flavorId}:${pin.hotspotWeight}` so a pin change invalidates the
 * trail (the coordinate space changes). In production N-OS uses uniform
 * L2 (hw=1 — see `weights.ts` and migration 260602_1), so `hotspotWeight`
 * is effectively a constant and the call site passes just `flavorId`.
 * If the pin's hw becomes user-toggleable again, callers must include
 * it in the resetKey or stale trail points will be drawn in the new
 * coordinate space.
 */
export function useFlavorSpaceTrail(
  current: { x: number; y: number } | null | undefined,
  resetKey: string,
): TrailPoint[] {
  const [trail, setTrail] = useState<TrailPoint[]>([]);
  const lastKey = useRef<string>(resetKey);
  const lastPoint = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (lastKey.current !== resetKey) {
      lastKey.current = resetKey;
      lastPoint.current = null;
      setTrail([]);
      return;
    }
    if (!current) return;
    if (
      lastPoint.current &&
      Math.abs(lastPoint.current.x - current.x) < MOVEMENT_EPSILON &&
      Math.abs(lastPoint.current.y - current.y) < MOVEMENT_EPSILON
    ) {
      return;
    }
    lastPoint.current = { x: current.x, y: current.y };
    setTrail((t) => {
      const next = [...t, { x: current.x, y: current.y, addedAt: Date.now() }];
      return next.length > TRAIL_CAP ? next.slice(next.length - TRAIL_CAP) : next;
    });
  }, [current?.x, current?.y, resetKey]);

  return trail;
}
